import { useEffect, useState, useRef } from "react";
import { Vote, Trophy } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { eventEmitter } from "../../context/TwitchContext";

function toggleLayout(active: boolean) {
  document
    .querySelector(".hot-word")
    ?.classList.toggle("hot-word-off", !active);
  document.querySelector(".chat")?.classList.toggle("chat-collapsed", active);
}

interface WordCount {
  word: string;
  count: number;
}

const HIDE_TIMEOUT = 15000;
const MIN_COUNT = 8;
const VEL_WINDOW = 3000;
const WINNER_TIMEOUT = 10000;

// DEV: __hotwordTest()
if (globalThis.window !== undefined) {
  (globalThis as any).__hotwordTest = () => {
    const emit = (msg: string, user = "padrebobo") =>
      eventEmitter.emit("twitchMessage", {
        tags: { "display-name": user, "user-id": user },
        message: msg,
        channel: "#padrebobo",
        timestamp: new Date(),
      });
    emit("!hotword");
    let i = 0;
    const words = ["igen", "nem", "igen", "igen"];
    setTimeout(() => {
      const iv: ReturnType<typeof setInterval> = setInterval(() => {
        if (i++ >= 150) return clearInterval(iv);
        emit(
          words[Math.floor(Math.random() * words.length)],
          "user" + (Math.floor(Math.random() * 3434) + 1),
        );
      }, 120);
    }, 3000);
  };
}

function countWords(
  words: WordCount[],
  message: string,
  ts: Map<string, number[]>,
) {
  const now = Date.now();
  for (const part of message.trim().split(/\s+/)) {
    const w = part.toLowerCase();
    if (!w) continue;
    const idx = words.findIndex((x) => x.word === w);
    if (idx === -1) words.push({ word: w, count: 1 });
    else words[idx].count++;
    ts.set(w, [...(ts.get(w) || []), now]);
  }
}

function rowColors(isWinner: boolean, velocity: number) {
  if (isWinner)
    return {
      bar: "#fb923c",
      shadow: "0 0 12px rgba(251,146,60,0.6)",
      label: "#fb923c",
      vote: "#fb923c",
      shake: false,
    };
  if (velocity > 5)
    return {
      bar: "#b91c1c",
      shadow: "0 0 24px rgba(185,28,28,1), 0 0 8px rgba(239,68,68,0.8)",
      label: "#f87171",
      vote: "#f87171",
      shake: true,
    };
  if (velocity > 3)
    return {
      bar: "#ef4444",
      shadow: "0 0 16px rgba(239,68,68,0.8)",
      label: "#fca5a5",
      vote: "#fca5a5",
      shake: false,
    };
  if (velocity > 1)
    return {
      bar: "#f97316",
      shadow: "0 0 10px rgba(249,115,22,0.5)",
      label: "#fdba74",
      vote: "#fdba74",
      shake: false,
    };
  return {
    bar: "#38bdf8",
    shadow: "none",
    label: "#e0f2fe",
    vote: "#7dd3fc",
    shake: false,
  };
}

function getVelocity(ts: Map<string, number[]>, word: string): number {
  const now = Date.now();
  const recent = (ts.get(word) || []).filter((t) => now - t < VEL_WINDOW);
  ts.set(word, recent);
  return recent.length / (VEL_WINDOW / 1000);
}

export default function Hotword() {
  const [words, setWords] = useState<WordCount[]>([]);
  const [visible, setVisible] = useState(false);
  const [winner, setWinner] = useState<string | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [, setTick] = useState(0); // NOSONAR
  const activeRef = useRef(false);
  const wordsRef = useRef<WordCount[]>([]);
  const tsRef = useRef(new Map<string, number[]>());
  const hideRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const winnerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const clear = () => {
      if (hideRef.current) clearTimeout(hideRef.current);
      if (tickRef.current) clearInterval(tickRef.current);
      if (winnerRef.current) clearTimeout(winnerRef.current);
      hideRef.current = null;
      tickRef.current = null;
      winnerRef.current = null;
    };

    const deactivate = () => {
      toggleLayout(false);
      activeRef.current = false;
      setVisible(false);
      setWinner(null);
      wordsRef.current = [];
      tsRef.current.clear();
      setWords([]);
      clear();
    };

    const activate = () => {
      toggleLayout(true);
      activeRef.current = true;
      wordsRef.current = [];
      tsRef.current.clear();
      setWords([]);
      setWinner(null);
      setVisible(true);
      playSound();
      setTimeout(() => setShowBanner(true), 500);
      setTimeout(() => setShowBanner(false), 3000);
      tickRef.current = setInterval(() => setTick((t) => t + 1), 1000);
    };

    function playSound() {
      const audio = new Audio("/reveal3.mp3");
      audio.volume = 0.2;
      audio.play().catch((err) => {
        console.error("Sound playback failed:", err);
      });
    }

    const handle = (data: {
      tags: Record<string, string>;
      message: string;
      channel: string;
      timestamp: Date;
    }) => {
      const username = (data.tags["display-name"] ?? "").toLowerCase();
      if (
        username === "padrebobo" &&
        data.message.toLowerCase().includes("!hotword")
      ) {
        activeRef.current ? deactivate() : activate();
        return;
      }
      if (!activeRef.current) return;

      if (hideRef.current) clearTimeout(hideRef.current);
      if (winnerRef.current) clearTimeout(winnerRef.current);
      setVisible(true);
      setWinner(null);
      hideRef.current = setTimeout(() => setVisible(false), HIDE_TIMEOUT);

      countWords(wordsRef.current, data.message, tsRef.current);
      setWords([...wordsRef.current]);

      winnerRef.current = setTimeout(() => {
        const top = [...wordsRef.current]
          .filter((w) => w.count >= MIN_COUNT)
          .sort((a, b) => b.count - a.count)[0];
        if (top) setWinner(top.word);
        setTimeout(() => toggleLayout(false), 1500);
      }, WINNER_TIMEOUT);
    };

    eventEmitter.on("twitchMessage", handle);
    return () => {
      eventEmitter.off("twitchMessage", handle);
      clear();
    };
  }, []);

  const display = words
    .filter((w) => w.count >= MIN_COUNT)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);
  const total = display.reduce((s, w) => s + w.count, 0);

  const bannerTitle = "HOTWORD ACTIVE";
  const bannerSub = "WRITE IN CHAT TO VOTE!";

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.2 } }}
          >
            <motion.div
              className="flex"
              style={{ filter: "drop-shadow(0 0 14px rgba(168,85,247,0.8))" }}
            >
              {bannerTitle.split("").map((ch, i) => (
                <motion.span
                  key={`${ch}-${i}`}
                  className="text-4xl font-black tracking-tight"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    background:
                      "linear-gradient(135deg, #e879f9 0%, #a855f7 50%, #818cf8 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                  initial={{ y: 40, opacity: 0, rotate: -8 }}
                  animate={{ y: 0, opacity: 1, rotate: 0 }}
                  transition={{
                    delay: i * 0.045,
                    type: "spring",
                    stiffness: 400,
                    damping: 18,
                  }}
                >
                  {ch === " " ? "\u00a0" : ch}
                </motion.span>
              ))}
            </motion.div>
            <motion.p
              className="text-base font-semibold text-purple-300 tracking-widest uppercase"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: bannerTitle.length * 0.045 + 0.1,
                duration: 0.4,
              }}
            >
              {bannerSub}
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        className="absolute inset-0 z-10"
        initial={{ y: -100, opacity: 0 }}
        animate={{
          y: visible && display.length > 0 ? 0 : -100,
          opacity: visible && display.length > 0 ? 1 : 0,
        }}
        transition={{
          duration: 0.5,
          ease: [0.22, 1, 0.36, 1],
          delay: showBanner ? 2.5 : 0,
        }}
        style={{
          pointerEvents: visible && display.length > 0 ? "auto" : "none",
        }}
      >
        <div className="bg-zinc-900/95 rounded-2xl shadow-2xl backdrop-blur-sm w-full h-full flex flex-col px-6 pt-5 pb-6">
          {/* Header */}
          <div className="flex items-center justify-center gap-2 mb-5 shrink-0">
            <span
              className="inline-flex rounded-full h-3 w-3 bg-red-500"
              style={{ animation: "dot-ping 1.4s ease-out infinite" }}
            />
            <span className="text-zinc-400 text-xs font-bold uppercase tracking-widest">
              Hotword
            </span>
          </div>

          {/* Rows — equal vertical distribution */}
          <div className="flex flex-col flex-1 gap-4">
            {display.map((row, i) => {
              const pct = Math.round((row.count / total) * 100);
              const velocity = getVelocity(tsRef.current, row.word);
              const isWinner = winner === row.word;
              const c = rowColors(isWinner, velocity);
              return (
                <div
                  key={row.word}
                  className="flex flex-col flex-1 justify-center gap-2"
                  style={
                    c.shake
                      ? {
                          animation: "hotbar-shake 0.4s ease-in-out infinite",
                        }
                      : undefined
                  }
                >
                  {/* Label row */}
                  <div className="flex items-baseline justify-between">
                    <div className="flex items-baseline gap-3">
                      <span
                        className="text-sm font-bold w-6 shrink-0"
                        style={{
                          color: isWinner ? "#fb923c" : "#52525b",
                          transition: "color 0.9s ease",
                        }}
                      >
                        {isWinner ? (
                          <Trophy className="w-5 h-5 inline-block" />
                        ) : (
                          `#${i + 1}`
                        )}
                      </span>
                      <span
                        className="text-lg font-black capitalize"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          color: c.label,
                          transition: "color 0.9s ease",
                        }}
                      >
                        {row.word}
                      </span>
                    </div>
                    <span
                      className="flex items-center gap-1.5 text-sm font-semibold shrink-0"
                      style={{
                        color: c.vote,
                        transition: "color 0.9s ease",
                      }}
                    >
                      <Vote className="w-4 h-4" />
                      {row.count}
                      <span className="text-xs opacity-60">({pct}%)</span>
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div
                    className="w-full bg-zinc-800 rounded-full h-2.5 overflow-hidden"
                    style={
                      c.shake
                        ? {
                            animation: "hotbar-shake 0.4s ease-in-out infinite",
                            animationDelay: "10ms",
                          }
                        : undefined
                    }
                  >
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${pct}%`,
                        transition:
                          "width 0.8s ease-in-out, background-color 0.9s ease, box-shadow 0.5s ease",
                        backgroundColor: c.bar,
                        boxShadow: c.shadow,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </>
  );
}
